import Konva from 'konva'
import { SourceHolder } from './CanvasElementSourceProviders/SourceProviderFactory'
import { computed, ComputedRef, watch, WatchHandle, shallowReactive } from 'vue'
import { KonvaEventObject } from 'konva/lib/Node'
import { sourceProviderFactories } from '@/components/canvas/CanvasElementSourceProviders/Factories'
import { useCamerasStore } from '@/store/cameras'
import { Camera } from '@/types/Cameras'
import { useLogsStore } from '@/store/logs'

const backgroundImageUrl = new URL('@/assets/camera-background_grayscale.png', import.meta.url).href
const videoOffImageUrl = new URL('@/assets/video-off-outline.svg', import.meta.url).href
const sharedBackgroundImage = new Image()
sharedBackgroundImage.src = backgroundImageUrl

const sharedErrorImage = new Image()
sharedErrorImage.src = videoOffImageUrl

export class CanvasElementProvider {
  protected readonly cameraId: string
  private reactiveState: { scale: number; timestamp: number; providerType: string; camera: Camera | null } = shallowReactive({
    scale: 1,
    timestamp: Date.now(),
    providerType: 'stillFrame',
    camera: null,
  })
  private readonly x: number
  private readonly y: number
  private readonly width: number
  private readonly height: number
  private readonly rotation: number
  private image!: HTMLImageElement
  private konvaElement!: Konva.Image
  private errorElement!: Konva.Image
  private abortController?: AbortController
  private loadingIndicator!: Konva.Arc
  private loadingAnimation!: Konva.Animation
  private overlayGroup!: Konva.Group
  private loadingCamera: boolean = false
  private readonly draggable: boolean = false
  private watchHandles: WatchHandle[] = []
  private readonly showTooltip!: (params: { x: number; y: number; text: string }) => void
  private readonly hideTooltip!: () => void
  group: Konva.Group

  constructor(
    providerType: string,
    timestamp: number,
    cameraId: string,
    width: number,
    height: number,
    scale: number,
    x: number,
    y: number,
    rotation: number,
    group: Konva.Group,
    draggable: boolean,
    showTooltip: (params: { x: number; y: number; text: string }) => void,
    hideTooltip: () => void
  ) {
    this.reactiveState.providerType = providerType
    this.reactiveState.timestamp = timestamp
    this.cameraId = cameraId
    this.reactiveState.scale = scale
    this.x = x
    this.y = y
    this.width = width
    this.height = height
    this.rotation = rotation
    this.group = group
    this.draggable = draggable
    this.showTooltip = showTooltip
    this.hideTooltip = hideTooltip

    this.loadCamera()
    this.buildElement()
    this.buildIndicators()
    this.attachWatchers()
    this.attachEventHandlers()
  }

  loadCamera() {
    const camerasStore = useCamerasStore()
    this.reactiveState.camera = camerasStore.getCameraById(undefined, this.cameraId)
    if (!this.reactiveState.camera) {
      this.loadingCamera = true
      camerasStore
        .loadCamera(this.cameraId)
        .then(result => {
          this.reactiveState.camera = result
        })
        .catch(e => {
          useLogsStore().addLogEntry({
            level: 'ERROR',
            message: `Error loading camera with id ${this.cameraId}`,
            error: e,
            tag: 'canvas',
          })
        })
        .finally(() => {
          this.loadingCamera = false
        })
    }
  }

  updateTimestamp(timestamp: number) {
    this.reactiveState.timestamp = timestamp
  }

  updateScale(scale: number) {
    this.reactiveState.scale = scale
  }

  private buildElement() {
    this.image = new Image()
    this.konvaElement = new Konva.Image({
      stroke: 'blue',
      strokeWidth: 0.15,
      strokeEnabled: false,
      image: this.image,
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      rotation: this.rotation,
      preventDefault: this.draggable,
    })
    this.konvaElement.setAttrs({ cameraId: this.cameraId })
    this.group.add(this.konvaElement)
    this.image.onload = () => {
      this.konvaElement.getLayer()?.batchDraw()
    }
  }

  public getKonvaElement(): Konva.Image {
    return this.konvaElement
  }
  public enableBorders() {
    this.konvaElement.strokeEnabled(true)
  }

  public disableBorders() {
    this.konvaElement.strokeEnabled(false)
  }

  private buildIndicators() {
    const transform = new Konva.Transform()
    transform.translate(this.x, this.y)
    transform.rotate(this.rotation * (Math.PI / 180))

    this.overlayGroup = new Konva.Group({
      draggable: this.draggable,
    })
    this.overlayGroup.preventDefault(this.draggable)
    this.group.add(this.overlayGroup)
    const elementCenterPoint = transform.point({
      x: this.width / 2,
      y: this.height / 2,
    })

    const loadingCircleRadius = Math.min(this.width, this.height) / 3
    this.loadingIndicator = new Konva.Arc({
      innerRadius: loadingCircleRadius - loadingCircleRadius / 15,
      outerRadius: loadingCircleRadius,
      x: elementCenterPoint.x,
      y: elementCenterPoint.y,
      stroke: 'blue',
      strokeWidth: loadingCircleRadius / 15,
      angle: 60,
      rotation: -45,
      lineCap: 'round',
      listening: false,
      lineJoin: 'round',
      shadowColor: '#3498db',
      shadowBlur: loadingCircleRadius / 3,
      shadowOpacity: 1,
      preventDefault: this.draggable,
    })

    this.loadingIndicator.cache({
      x: -loadingCircleRadius * 1.5,
      y: -loadingCircleRadius * 1.5,
      width: loadingCircleRadius * 3,
      height: loadingCircleRadius * 3,
      pixelRatio: 50,
    })

    this.loadingAnimation = new Konva.Animation(frame => {
      const diff = frame?.timeDiff || 0
      this.loadingIndicator.rotation(this.loadingIndicator.rotation() + diff * 0.2)

      this.loadingIndicator.getLayer()?.batchDraw()
      return false
    })

    this.errorElement = new Konva.Image({
      image: sharedErrorImage,
      x: elementCenterPoint.x - loadingCircleRadius,
      y: elementCenterPoint.y - loadingCircleRadius,
      width: loadingCircleRadius * 2,
      height: loadingCircleRadius * 2,
      listening: false,
      preventDefault: this.draggable,
    })
  }

  private isLoading(): boolean {
    return this.sourceHolder.value?.isLoading.value || this.loadingCamera
  }

  private attachWatchers() {
    this.watchHandles.push(
      watch(
        () => [this.reactiveState.camera?.cameraId, this.reactiveState.timestamp, this.reactiveState.providerType, this.reactiveState.scale],
        (current, previous) => {
          this.renderSource(current[1] !== previous[1])
        }
      )
    )

    this.watchHandles.push(
      watch(
        () => [this.sourceHolder.value?.currentSource, this.sourceHolder.value?.isLoading],
        () => {
          //Source is still loading and there's no previous image to show -> Default to Background Image URL, remove Error Indicator
          if (this.isLoading() && !this.image.src) {
            this.hideErrorElement()

            this.image.src = backgroundImageUrl
            return
          }

          if (!this.isLoading()) {
            //Nothing is currently loading, and there's a source to show, -> Show the source, remove Error Indicator
            if (this.sourceHolder.value?.currentSource.value) {
              this.hideErrorElement()
              this.image.src = this.sourceHolder.value?.currentSource.value
            } else {
              //Nothing is currently loading, no source is available -> show Background Image and Error Indicator
              this.image.src = backgroundImageUrl
              this.showErrorElement()
            }
          } else {
            //Source is loading and a previous source is avalilable -> remove error indicator, keep showing previous source
            this.hideErrorElement()
          }
        },

        { immediate: true, deep: true }
      )
    )

    this.watchHandles.push(
      watch(
        () => [this.sourceHolder.value?.isLoading],
        () => {
          if (this.isLoading()) {
            this.overlayGroup.add(this.loadingIndicator)
            this.loadingAnimation.start()
          } else {
            this.loadingAnimation.stop()
            this.loadingIndicator.remove()
          }
        },
        { immediate: true, deep: true }
      )
    )
  }

  private hideErrorElement() {
    if (this.errorElement.getParent() === this.overlayGroup) {
      this.errorElement.remove()
      this.overlayGroup.getLayer()?.batchDraw()
    }
  }

  private showErrorElement() {
    if (this.errorElement.getParent() !== this.overlayGroup) {
      this.overlayGroup.add(this.errorElement)
      this.overlayGroup.getLayer()?.batchDraw()
    }
  }

  private attachEventHandlers() {
    this.konvaElement.on('dblclick', (e: KonvaEventObject<MouseEvent, Konva.Image>) => {
      e.evt.preventDefault()
      this.sourceHolder.value?.ondblclick()
    })
    this.konvaElement.on('contextmenu', (e: KonvaEventObject<PointerEvent, Konva.Image>) => {
      e.evt.preventDefault()
      this.sourceHolder.value?.onContextMenu()
    })

    this.konvaElement.on('mouseenter', (e: KonvaEventObject<MouseEvent, Konva.Image>) => {
      if (this.sourceHolder.value?.canShowTooltip()) {
        const tooltipText = this.sourceHolder.value.getTooltipText()
        this.showTooltip({
          x: e.evt.clientX + 2,
          y: e.evt.clientY + 2,
          text: tooltipText,
        })
      }
    })

    this.konvaElement.on('mouseout', () => {
      this.hideTooltip()
    })
    this.konvaElement.on('mousemove', (e: KonvaEventObject<MouseEvent, Konva.Image>) => {
      if (this.sourceHolder.value?.canShowTooltip()) {
        const tooltipText = this.sourceHolder.value.getTooltipText()
        this.showTooltip({
          x: e.evt.clientX + 2,
          y: e.evt.clientY + 2,
          text: tooltipText,
        })
      }
    })
  }

  private sourceHolder: ComputedRef<SourceHolder<object> | null> = computed(() => {
    if (!this.reactiveState.camera) return null
    if (!this.reactiveState.timestamp) return null
    if (!this.cameraId) return null
    if (!this.reactiveState.providerType) return null
    return sourceProviderFactories[this.reactiveState.providerType].getOrCreateSourceHolder(
      this.cameraId,
      this.reactiveState.timestamp,
      this.reactiveState.camera
    )
  })

  private renderSource(timestampChanged: boolean = false) {
    if (!this.reactiveState.camera) return
    if (!this.sourceHolder.value) return

    if (timestampChanged) {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = undefined
      }
    }
    if (!this.abortController) {
      this.abortController = new AbortController()
    }
    this.sourceHolder.value.ensureSourceIsLoaded(this.reactiveState.scale * this.height, this.abortController.signal, this.isInView())
  }

  updatePosition() {
    this.konvaElement.getLayer()?.batchDraw()
  }

  isInView(): boolean {
    if (!this.konvaElement) return false
    return this.konvaElement.isClientRectOnScreen()
  }

  setProviderType(type: string) {
    this.reactiveState.providerType = type
  }

  destroyElement() {
    while (this.watchHandles.length) {
      const unwatch = this.watchHandles.pop()
      if (unwatch) unwatch()
    }
    if (this.abortController) this.abortController.abort()
    this.konvaElement.destroy()
    this.errorElement.destroy()
    this.loadingAnimation.stop()
    this.loadingIndicator.destroy()
    this.overlayGroup.destroy()
    this.group.destroy()
  }
}
